import { GraphQLSchema, isAbstractType, isNamedType } from 'graphql';

import { assertUnreachable } from 'src/lib/assertUnreachable';
import {
  GraphQLNode,
  RootTypeNames,
  getRootTypeNames,
  unwrapType,
} from 'src/lib/graphql/graphqlHelpers';

import { isOperationDefinition } from './graphqlHelpers';
import { ParseState } from './graphqlParseHelpers';

// There are four kinds of paths we deal with here. This file exports
// the types for each of them, and methods to convert between them.
//
// OperationPath - A serializable set of simple objects that describes a path:
//   Use this whenever we want to store or pass around a path
//
// SerializedPath - The serialized version of an OperationPath
//
// GraphQLPath - A set of graphql-js nodes representing each node in the path:
//   Use this to show additional information from the schema about any point
//   in the path
//
// ParsePath - A set of states from graphql-language-service:
//   This comes from the fault tolerant parser

// Serializable description of a path
export type OperationPath = OperationPathSegment[];
export type OperationPathSegment =
  | { kind: 'Query' | 'ShortQuery' | 'Mutation' | 'Subscription' }
  | { kind: 'FragmentDefinition'; type: string }
  | { kind: 'InlineFragment'; type: string | null }
  | { kind: 'Field' | 'AliasedField'; name: string; alias?: string }
  | { kind: 'Argument'; name: string };

type SerializedPath = string;

// Path of graphql-js nodes
export type GraphQLPath = GraphQLNode[];

// A parse state has ancestors for the previous nodes in the path
type ParsePath = ParseState[];

// Conversions

/**
 * Convert a ParsePath to an OperationPath
 *
 * Go through the parent tree for our node, adding relevant nodes to the
 * path to this offset
 */
export function parsePathToOperationPath(parsePath: ParsePath): OperationPath {
  return parsePath
    .map((parseState: ParseState) => {
      if (isOperationDefinition(parseState)) {
        return { kind: parseState.kind };
      }
      switch (parseState.kind) {
        case 'FragmentDefinition': {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return { kind: 'FragmentDefinition', type: parseState.type! };
        }
        case 'InlineFragment': {
          return { kind: 'InlineFragment', type: parseState.type };
        }
        case 'Field':
        case 'AliasedField': {
          if (parseState.name) {
            return {
              kind: parseState.kind,
              name: parseState.name,
              alias: parseState.alias ?? undefined,
            };
          }
          return undefined;
        }
        case 'Argument': {
          return { kind: 'Argument', name: parseState.name };
        }
        default:
          // Ignore node
          return undefined;
      }
    })
    .filter((node): node is OperationPathSegment => !!node);
}

/**
 * Convert an OperationPath to a GraphQLPath
 *
 * If the given path does not match the schema, return null
 */
export function operationPathToGraphQLPath(
  operationPath: OperationPath,
  schema: GraphQLSchema,
): GraphQLPath | null {
  // Reduce this path string representation to the actual schema nodes it
  // represents.
  return operationPath.reduce(
    (
      graphQLPath: GraphQLPath | null,
      segment: OperationPathSegment,
    ): GraphQLPath | null => {
      if (!graphQLPath) {
        return graphQLPath;
      }
      if (isOperationDefinition(segment)) {
        // Operation definition should be the first node
        if (graphQLPath.length !== 0) {
          return null;
        }

        const operationType =
          segment.kind === 'Query' || segment.kind === 'ShortQuery'
            ? schema.getQueryType()
            : segment.kind === 'Mutation'
              ? schema.getMutationType()
              : segment.kind === 'Subscription'
                ? schema.getSubscriptionType()
                : null;
        if (!operationType) {
          return null;
        }
        return [operationType];
      }
      switch (segment.kind) {
        case 'FragmentDefinition': {
          // Fragment definition should be the first node
          if (graphQLPath.length !== 0) {
            return null;
          }
          const fragmentType = schema.getType(segment.type);
          if (!fragmentType) {
            return null;
          }
          return [fragmentType];
        }
        case 'Field':
        case 'AliasedField': {
          const previousNode = graphQLPath.slice(-1)[0];
          if (!previousNode) return null;
          const previousType = unwrapType(previousNode);
          if (!('getFields' in previousType)) {
            return null;
          }
          const nextNode = previousType.getFields()[segment.name];
          if (!nextNode) {
            return null;
          }
          return [...graphQLPath, nextNode];
        }
        case 'InlineFragment': {
          const previousNode = graphQLPath.slice(-1)[0];
          if (!previousNode) return null;
          const previousType = unwrapType(previousNode);

          // If the fragment is on the current type ignore it
          if (previousType.name === segment.type || !segment.type) {
            return graphQLPath;
          }

          if (!isAbstractType(previousType)) {
            return null;
          }
          const subTypes = Object.values(schema.getTypeMap()).filter(
            (otherType) => schema.isSubType(previousType, otherType),
          );

          const implementor = subTypes.find(
            (subType) => subType.name === segment.type,
          );
          if (!implementor) {
            return null;
          }

          return [...graphQLPath, implementor];
        }
        case 'Argument': {
          const previousNode = graphQLPath.slice(-1)[0];
          if (!previousNode) return null;
          if (!('args' in previousNode)) {
            return null;
          }
          const nextNode = previousNode.args.find(
            (arg) => arg.name === segment.name,
          );
          if (!nextNode) {
            return null;
          }
          return [...graphQLPath, nextNode];
        }
        default:
          throw assertUnreachable(segment);
      }
    },
    [],
  );
}

/**
 * Convert an OperationPath to a GraphQLPath
 *
 * If the given path does not match the schema, return null
 */
export function graphQLPathToOperationPath(
  graphQLPath: GraphQLPath,
  schema: GraphQLSchema,
): OperationPath {
  const rootTypeNames = getRootTypeNames(schema);

  return graphQLPath.map(
    (graphQLNode, i): OperationPathSegment =>
      graphQLNodeToOperationPathSegment(
        graphQLNode,
        rootTypeNames,
        i === 0,
        false,
      ),
  );
}

export function graphQLNodeToOperationPathSegment(
  graphQLNode: GraphQLNode,
  rootTypes: RootTypeNames,
  isStartingNode: boolean = false,
  isArgument: boolean,
): OperationPathSegment {
  if (isNamedType(graphQLNode)) {
    if (graphQLNode.name === rootTypes.query) {
      return { kind: 'Query' };
    }
    if (graphQLNode.name === rootTypes.mutation) {
      return { kind: 'Mutation' };
    }
    if (graphQLNode.name === rootTypes.subscription) {
      return { kind: 'Subscription' };
    }
    return {
      kind: isStartingNode ? 'FragmentDefinition' : 'InlineFragment',
      type: graphQLNode.name,
    };
  }

  return {
    kind: isArgument ? 'Argument' : 'Field',
    name: graphQLNode.name,
  };
}

/**
 * Serialize an operation path
 */
export function serializeOperationPath(
  operationPath: OperationPath,
): SerializedPath {
  return operationPath
    .map((segment) => {
      if (isOperationDefinition(segment)) {
        return `operation:${
          segment.kind === 'ShortQuery' ? 'query' : segment.kind.toLowerCase()
        }`;
      }
      switch (segment.kind) {
        case 'Field':
        case 'AliasedField':
          return `field:${segment.name}${
            segment.alias ? `@${segment.alias}` : ''
          }`;
        case 'FragmentDefinition':
          return `fragment:${segment.type}`;
        case 'InlineFragment':
          return `inlineFragment:${segment.type}`;
        case 'Argument':
          return `argument:${segment.name}`;
        default:
          return assertUnreachable(segment);
      }
    })
    .join('/');
}

/**
 * Deserialize an operation path
 */
export function deserializeOperationPath(
  serializedPath: SerializedPath,
): OperationPath {
  return serializedPath.split('/').map((fullSegment): OperationPathSegment => {
    const [segmentType, segment] = fullSegment.split(':');
    switch (segmentType) {
      case 'operation': {
        switch (segment) {
          case 'query':
            return { kind: 'Query' };
          case 'mutation':
            return { kind: 'Mutation' };
          default:
            return { kind: 'Subscription' };
        }
      }
      case 'fragment':
        // TODO: non null assertion added while migrating to typescript 5.0.4, we should remove it
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return { kind: 'FragmentDefinition', type: segment! };
      case 'field': {
        // TODO: non null assertion added while migrating to typescript 5.0.4, we should remove it
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const [name, alias] = segment!.split('@');
        if (alias) {
          return { kind: 'AliasedField', name, alias };
        }
        return { kind: 'Field', name };
      }
      case 'inlineFragment':
        return {
          kind: 'InlineFragment',
          // TODO: non null assertion added while migrating to typescript 5.0.4, we should remove it
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          type: segment === 'null' ? null : segment!,
        };
      case 'argument':
        // TODO: non null assertion added while migrating to typescript 5.0.4, we should remove it
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return { kind: 'Argument', name: segment! };
      default:
        throw new Error(`Bad segment type '${segmentType}'`);
    }
  });
}
